import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  NgZone,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { TabsModule } from 'primeng/tabs';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageService, ConfirmationService } from 'primeng/api';
import { PostoService } from '@core/services/domain-services';
import { BackButtonComponent } from '@shared/components/back-button/back-button.component';
import { StatusBadgeComponent } from '@shared/components/status-badge/status-badge.component';
import { Posto } from '@core/models/models';
import { PostoInfoTabComponent } from './tabs/posto-info-tab/posto-info-tab.component';
import { PostoEfetivosAlocadosTabComponent } from './tabs/posto-efetivos-alocados-tab/posto-efetivos-alocados-tab.component';
import { PostoEscalaTabComponent } from './tabs/posto-escala-tab/posto-escala-tab.component';
import { PostoCheckpointsTabComponent } from './tabs/posto-checkpoints-tab/posto-checkpoints-tab.component';
import { PostoEquipamentosTabComponent } from './tabs/posto-equipamentos-tab/posto-equipamentos-tab.component';

@Component({
  selector: 'app-posto-detail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    TabsModule,
    SkeletonModule,
    TooltipModule,

    BackButtonComponent,
    StatusBadgeComponent,
    PostoInfoTabComponent,
    PostoEfetivosAlocadosTabComponent,
    PostoCheckpointsTabComponent,
    PostoEquipamentosTabComponent,
    PostoEscalaTabComponent,
  ],

  templateUrl: './posto-detail.component.html',
  styleUrl: './posto-detail.component.scss',
})
export class PostoDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  readonly router = inject(Router);
  private readonly postoSvc = inject(PostoService);
  private readonly msgService = inject(MessageService);
  private readonly confirm = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);

  posto: Posto | null = null;
  currentPostoId = 0;
  loadingPosto = false;

  activeTab = '0';
  openScaleDrawerOnInit = false;
  suspendendo = false;

  efetivosAlocadosCount: number | null = null;
  escalasCount: number | null = null;
  checkpointsCount: number | null = null;
  equipamentosCount: number | null = null;

  ngOnInit(): void {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const id = Number(params.get('id'));
        if (id) {
          const queryParams = this.route.snapshot.queryParamMap;
          const abrirEscala = queryParams.get('tab') === 'escala';
          this.activeTab = abrirEscala ? '4' : '0';
          this.openScaleDrawerOnInit =
            abrirEscala && queryParams.get('acao') === 'criar';
          this.resetTabSummaries();
          this.currentPostoId = id;
          this.loadPosto(id);
        }
      });
  }

  loadPosto(id: number): void {
    this.loadingPosto = true;
    this.cdr.detectChanges();

    this.postoSvc
      .getById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (p) => {
          this.zone.run(() => {
            this.posto = p;
            this.efetivosAlocadosCount = p.totalEfetivosAlocados ?? 0;
            this.escalasCount = p.totalEscalas ?? 0;
            this.checkpointsCount = p.totalCheckpoints ?? 0;
            this.equipamentosCount = p.totalEquipamentos ?? 0;
            this.loadingPosto = false;
            this.cdr.detectChanges();
          });
        },
        error: () => {
          this.zone.run(() => {
            this.posto = null;
            this.msgService.add({
              severity: 'error',
              summary: 'Erro',
              detail: 'Não foi possível carregar os dados do posto.',
              life: 4000,
            });
            this.loadingPosto = false;
            this.cdr.detectChanges();
          });
        },
      });
  }

  editarPosto(): void {
    this.router.navigate(['/admin/postos', this.currentPostoId, 'editar']);
  }

  suspenderPosto(): void {
    if (!this.posto) return;
    const novoEstado = !this.posto.ativo;
    this.confirm.confirm({
      header: novoEstado ? 'Activar Posto' : 'Suspender Posto',
      message: novoEstado
        ? `Activar o posto "${this.posto.nome}"?`
        : `Suspender o posto "${this.posto.nome}"? Ficara inactivo para novas escalas.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: novoEstado ? 'Activar' : 'Suspender',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: novoEstado ? '' : 'p-button-danger',
      accept: () => {
        this.suspendendo = true;
        this.postoSvc
          .toggleStatusPosto(this.currentPostoId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.zone.run(() => {
                if (this.posto)
                  this.posto = { ...this.posto, ativo: novoEstado };
                this.msgService.add({
                  severity: novoEstado ? 'success' : 'warn',
                  summary: novoEstado ? 'Posto activado' : 'Posto suspenso',
                  detail: 'Operacao realizada com sucesso',
                  life: 3000,
                });
                this.suspendendo = false;
                this.cdr.detectChanges();
              });
            },
            error: () => {
              this.zone.run(() => {
                this.msgService.add({
                  severity: 'error',
                  summary: 'Erro',
                  detail: 'Falha ao alterar estado',
                  life: 3000,
                });
                this.suspendendo = false;
                this.cdr.detectChanges();
              });
            },
          });
      },
    });
  }

  navegarParaNovaEscala(): void {
    this.router.navigate(['/admin/escalas/nova'], {
      queryParams: { idPosto: this.currentPostoId },
    });
  }

  onEscalasCountChange(count: number): void {
    this.escalasCount = count;
    this.cdr.markForCheck();
  }

  onScaleCreateDrawerOpened(): void {
    if (!this.openScaleDrawerOnInit) return;

    this.openScaleDrawerOnInit = false;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { acao: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  onEfetivosCountChange(count: number): void {
    this.efetivosAlocadosCount = count;
    this.cdr.markForCheck();
  }

  onCheckpointsCountChange(count: number): void {
    this.checkpointsCount = count;
    this.cdr.markForCheck();
  }

  onEquipamentosCountChange(count: number): void {
    this.equipamentosCount = count;
    this.cdr.markForCheck();
  }

  private resetTabSummaries(): void {
    this.efetivosAlocadosCount = null;
    this.escalasCount = null;
    this.checkpointsCount = null;
    this.equipamentosCount = null;
  }

  formatDate(d: string | Date | undefined): string {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('pt-AO');
  }

  formatKz(v?: number): string {
    if (v == null) return '-';
    return new Intl.NumberFormat('pt-AO', {
      style: 'currency',
      currency: 'AOA',
      maximumFractionDigits: 0,
    }).format(v);
  }
}
